import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage, LANGUAGES } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Menu, X, Sun, Moon, Globe, ChevronDown } from 'lucide-react';

const Header = () => {
  const { t, language, changeLanguage, isRTL, currentLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  // Get localized path
  const getLocalizedPath = (path) => {
    return language === 'en' ? path : `/${language}${path}`;
  };

  return (
    <header className="header" dir={isRTL ? 'rtl' : 'ltr'}>
      <a href="#main-content" className="skip-link" data-testid="skip-link">
        {t('a11y.skipToContent')}
      </a>
      
      <div className="header-container">
        <Link to="/" className="logo" data-testid="logo">
          <span className="logo-icon" aria-hidden="true">🎡</span>
          <span className="logo-text">wheel.expert</span>
        </Link>

        <nav className="nav-desktop" aria-label="Main navigation">
          <Link to="/" className="nav-link" data-testid="nav-home">{t('nav.home')}</Link>
          <Link to={getLocalizedPath('/wheels')} className="nav-link" data-testid="nav-variants">{t('nav.variants')}</Link>
          <Link to={getLocalizedPath('/blog')} className="nav-link" data-testid="nav-blog">{t('nav.blog')}</Link>
          <Link to={getLocalizedPath('/faq')} className="nav-link" data-testid="nav-faq">{t('nav.faq')}</Link>
        </nav>

        <div className="header-actions">
          {/* Language Selector */}
          <div className="language-selector">
            <button
              onClick={() => setLangMenuOpen(!langMenuOpen)}
              className="btn-icon lang-btn"
              aria-label={t('a11y.languageSelect')}
              aria-expanded={langMenuOpen}
              data-testid="language-button"
            >
              <Globe size={20} />
              <span className="lang-current">{currentLanguage?.flag}</span>
              <ChevronDown size={14} className={langMenuOpen ? 'rotated' : ''} />
            </button>
            
            {langMenuOpen && (
              <div className="lang-dropdown" role="listbox" data-testid="language-dropdown">
                <div className="lang-dropdown-inner">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setLangMenuOpen(false);
                      }}
                      className={`lang-option ${language === lang.code ? 'active' : ''}`}
                      role="option"
                      aria-selected={language === lang.code}
                      data-testid={`lang-${lang.code}`}
                    >
                      <span className="lang-flag">{lang.flag}</span>
                      <span className="lang-name">{lang.nativeName}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="btn-icon theme-toggle"
            aria-label={t('a11y.themeToggle')}
            aria-pressed={theme === 'dark'}
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="btn-icon menu-toggle"
            aria-label={menuOpen ? t('a11y.menuClose') : t('a11y.menuOpen')}
            aria-expanded={menuOpen}
            data-testid="menu-toggle"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <nav className="nav-mobile" aria-label="Mobile navigation" data-testid="mobile-menu">
          <Link to="/" className="nav-link-mobile" onClick={() => setMenuOpen(false)}>
            {t('nav.home')}
          </Link>
          <Link to={getLocalizedPath('/wheels')} className="nav-link-mobile" onClick={() => setMenuOpen(false)}>
            {t('nav.variants')}
          </Link>
          <Link to={getLocalizedPath('/blog')} className="nav-link-mobile" onClick={() => setMenuOpen(false)}>
            {t('nav.blog')}
          </Link>
          <Link to={getLocalizedPath('/faq')} className="nav-link-mobile" onClick={() => setMenuOpen(false)}>
            {t('nav.faq')}
          </Link>
        </nav>
      )}

      {/* Click outside to close dropdowns */}
      {(langMenuOpen) && (
        <div 
          className="dropdown-overlay" 
          onClick={() => setLangMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  );
};

export default Header;
